/**
 * The P&L tab (TMMAPP-0006 step 4, design §7.5 / MM-D-39; TMMAPP-0111): the envelope alias of the
 * virtual account P&L (`GET /v1/envelopes/{id}/pnl`, the same `VirtualAccountPnl` shape; months
 * sent as `YYYY-MM`) for the shared window — four tiles (five on `md`+), the shared `PnlTable`
 * with a BUDGET envelope's Budget row, and the opening + net = closing reconciliation the API
 * asserts (a contradiction is surfaced, never corrected). TMMAPI-0147 serves no prior period, so
 * the comparison switch is gone.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { pnlApi, toMonth, type Pnl } from '@/api/pnl';
import { ErrorState } from '@/components/ErrorState';
import { MoneyText } from '@/components/MoneyText';
import { PnlTable } from '@/features/virtualAccount/PnlTable';
import { subtotalCells, totalOf } from '@/features/virtualAccount/pnlCells';
import type { WindowDates } from './window';

const SKELETON_KEYS = ['a', 'b', 'c', 'd'] as const;

type PnlState =
  { key: string; status: 'ready'; pnl: Pnl } | { key: string; status: 'error'; error: unknown };

interface PnlTabProps {
  envelopeId: number;
  /** The virtual account OWNER's currency (§14.1). */
  currency: string | null;
  /** The shared window's dates; `null` while a custom window is invalid (nothing is fetched). */
  dates: WindowDates | null;
}

export function PnlTab({ envelopeId, currency, dates }: PnlTabProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const [state, setState] = useState<PnlState | null>(null);
  const [tick, setTick] = useState(0);

  const from = dates?.from;
  const to = dates?.to;
  const key = dates === null ? null : `${envelopeId}|${from ?? ''}|${to ?? ''}|${tick}`;
  // StrictMode double-invokes the effect; a key is fetched once. Responses that outlive a later
  // request are dropped by the sequence.
  const lastKey = useRef<string | null>(null);
  const seq = useRef(0);

  const load = useCallback(async () => {
    if (key === null) return;
    const mine = ++seq.current;
    try {
      const pnl = await pnlApi.envelope(envelopeId, { from: toMonth(from), to: toMonth(to) });
      if (seq.current !== mine) return;
      setState({ key, status: 'ready', pnl });
    } catch (error) {
      if (seq.current !== mine) return;
      setState({ key, status: 'error', error });
    }
  }, [envelopeId, from, to, key]);

  useEffect(() => {
    if (key === null || lastKey.current === key) return;
    lastKey.current = key;
    void load();
  }, [key, load]);

  const ready = state !== null && state.key === key && state.status === 'ready' ? state.pnl : null;
  const failed = state !== null && state.key === key && state.status === 'error' ? state : null;
  const loading = key !== null && ready === null && failed === null;

  const tile = (label: string, minor: number | null) => (
    <Grid size={{ xs: 6, md: 'grow' }}>
      <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
        <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
          {label}
        </Typography>
        <MoneyText minor={minor} currency={currency} component="div" />
      </Paper>
    </Grid>
  );

  const empty =
    ready !== null &&
    ready.income.length === 0 &&
    ready.expense.length === 0 &&
    ready.uncategorized.inCells.every((c) => c === 0) &&
    ready.uncategorized.outCells.every((c) => c === 0);
  return (
    <Box>
      {failed !== null && (
        <ErrorState
          variant="section"
          error={failed.error}
          what={t('errors.what.pnl')}
          onRetry={() => setTick((n) => n + 1)}
        />
      )}

      {loading && (
        <Stack spacing={2} aria-busy="true">
          <Grid container spacing={1.5}>
            {SKELETON_KEYS.map((k) => (
              <Grid key={k} size={{ xs: 6, md: 3 }}>
                <Skeleton variant="rounded" height={64} />
              </Grid>
            ))}
          </Grid>
          <Skeleton variant="rounded" height={200} />
        </Stack>
      )}

      {ready !== null && (
        <Stack spacing={2}>
          <Grid container spacing={1.5}>
            {tile(t('envelope.pnlOpening'), ready.openingMinor)}
            {tile(
              t('envelope.pnlIncome'),
              totalOf(subtotalCells(ready.income, ready.uncategorized.inCells)),
            )}
            {tile(
              t('envelope.pnlExpense'),
              totalOf(subtotalCells(ready.expense, ready.uncategorized.outCells)),
            )}
            {tile(t('envelope.pnlNet'), ready.net.totalMinor)}
            {md && tile(t('envelope.pnlClosing'), ready.closingMinor)}
          </Grid>

          {empty ? (
            <Typography variant="body1">{t('envelope.pnlEmpty')}</Typography>
          ) : (
            <PnlTable
              pnl={ready}
              currency={currency}
              budgetEnvelopeId={envelopeId}
              label={t('envelope.tabPnl')}
            />
          )}

          <Typography variant="body2" component="p">
            <Trans
              i18nKey="envelope.pnlReconcile"
              components={{
                opening: <MoneyText minor={ready.openingMinor} currency={currency} />,
                net: <MoneyText minor={ready.net.totalMinor} currency={currency} />,
                closing: <MoneyText minor={ready.closingMinor} currency={currency} />,
              }}
            />
          </Typography>
          {ready.openingMinor + ready.net.totalMinor !== ready.closingMinor && (
            <Alert severity="warning">{t('envelope.pnlMismatch')}</Alert>
          )}
        </Stack>
      )}
    </Box>
  );
}
